import React, { useEffect, useState } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import axiosInstance from '../../services/axiosInterceptor'
import useSchoolSettings from '../../hooks/useSchoolSettings'
import { fmtDays } from '../hrShared'
import '../../print.css'
import './leaveForm.css'

// แบบใบลาป่วย ลากิจส่วนตัว ลาคลอดบุตร — ทำตามกระดาษที่โรงเรียนใช้อยู่
// ครูกดพิมพ์จากใบที่ยื่นไว้แล้ว ช่องที่ระบบมีข้อมูลถูกเติมให้ ส่วนช่องที่ไม่มีข้อมูล
// เป็นเส้นประให้เขียนมือ (ลายเซ็น และช่องคำสั่งเมื่อผู้อำนวยการยังไม่ได้พิจารณา)

const MONTHS_TH = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม']

// 'YYYY-MM-DD' -> { d, month, be } : หัวใบลามีช่องวัน/เดือน/ปี แยกกัน
// อ่านจากสตริงตรงๆ ไม่ผ่าน new Date() เพื่อไม่ให้เขตเวลาเลื่อนวัน
const parts = (s) => {
  if (!s) return null
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return null
  return { d, month: MONTHS_TH[m - 1], be: y + 543 }
}

const fmtDateTH = (s) => {
  const p = parts(s)
  return p ? `${p.d} ${p.month} ${p.be}` : ''
}

// 'ลาป่วย' -> 'ป่วย' : ฟอร์มพิมพ์คำว่า "ขอลา" ไว้ให้แล้ว
const leaveWord = (name) => String(name || '').replace(/^ลา/, '')

// ช่องเส้นประ — value ว่าง = เส้นเปล่าไว้เขียนมือ
const Fill = ({ value, grow = 1 }) => (
  <span className="lf-fill" style={{ flexGrow: grow }}>{value || ''}</span>
)

export default function LeaveFormDoc() {
  const { lrId } = useParams()
  const navigate = useNavigate()
  const school = useSchoolSettings()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    axiosInstance.get(`/api/hr/leave-requests/${lrId}/print`)
      .then(res => { if (res.data.success) setData(res.data.data) })
      .catch(err => setError(err.response?.data?.message || 'โหลดข้อมูลใบลาไม่สำเร็จ'))
  }, [lrId])

  if (error) {
    return (
      <div className="p-4">
        <div className="alert alert-danger">{error}</div>
        <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>กลับ</Button>
      </div>
    )
  }
  if (!data) {
    return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
  }

  const r = data.request
  const written = parts(r.lr_created_date)

  // "ในระหว่างลาติดต่อข้าพเจ้าได้ที่" — ที่อยู่กับเบอร์โทรจากประวัติบุคลากร
  const contact = [r.hp_address, r.t_phone && `โทร. ${r.t_phone}`].filter(Boolean).join('  ')

  // แถวตารางสถิติ: สามประเภทที่อยู่บนกระดาษเสมอ + ประเภทอื่นที่มีวันลาในปีนี้หรือเป็นใบนี้
  const FORM_ROWS = ['ลาป่วย', 'ลากิจส่วนตัว', 'ลาคลอดบุตร']
  const rows = data.stats.filter(s =>
    FORM_ROWS.includes(s.lt_name) ||
    Number(s.used_days) > 0 ||
    s.lt_id === r.tb_hr_leave_type_lt_id)

  const thisDays = parseFloat(r.lr_days) || 0
  const schoolShort = String(school.school_name || '').replace(/^โรงเรียน/, '')

  return (
    <div className="lf-wrap">
      {/* กฎตอนสั่งพิมพ์อยู่ตรงนี้ ไม่ใช่ในไฟล์ CSS เพราะ CSS ถูกรวมเข้า bundle ทั้งแอป
          แล้ว "body * { visibility: hidden }" จะทำให้พิมพ์หน้าอื่นได้กระดาษเปล่า */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .lf-wrap { background: #fff; padding: 0; }
          .lf-sheet, .lf-sheet * { visibility: visible; }
          .lf-sheet {
            position: absolute; left: 0; top: 0; width: 100%;
            min-height: 0; margin: 0; box-shadow: none; padding: 0.3cm 0.8cm;
          }
          .lf-toolbar { display: none; }
        }
        @page { size: A4 portrait; margin: 1cm; }
      `}</style>

      <div className="lf-toolbar">
        <Button variant="light" size="sm" onClick={() => navigate(-1)}>
          <i className="ri-arrow-left-line me-1" />กลับ
        </Button>
        <Button variant="primary" size="sm" onClick={() => window.print()}>
          <i className="ri-printer-line me-1" />พิมพ์
        </Button>
      </div>

      <div className="lf-sheet">
        <div className="lf-crest"><img src={school.logoSrc} alt="" /></div>
        <div className="lf-title">แบบใบลาป่วย ลากิจส่วนตัว ลาคลอดบุตร</div>

        <div className="lf-head">
          <div className="lf-line">
            <span>เขียนที่โรงเรียน</span><Fill value={schoolShort} />
          </div>
          <div className="lf-line">
            <span>วันที่</span><Fill value={written && written.d} grow={0.4} />
            <span>เดือน</span><Fill value={written && written.month} />
            <span>พ.ศ.</span><Fill value={written && written.be} grow={0.5} />
          </div>
        </div>

        <div className="lf-line lf-plain">
          <span className="lf-label">เรื่อง</span>
          <span>ขอลา</span><Fill value={leaveWord(r.lt_name)} grow={0} />
        </div>
        <div className="lf-line lf-plain">
          <span className="lf-label">เรียน</span>
          <span>ผู้อำนวยการ{school.school_name}</span>
        </div>

        <div className="lf-line lf-indent">
          <span>ข้าพเจ้า</span><Fill value={r.t_name} grow={1.4} />
          <span>ตำแหน่ง</span><Fill value={r.hp_position_title} />
          <span>เลขประจำตัวครู</span><Fill value={r.t_idnumber} grow={0.7} />
        </div>
        <div className="lf-line">
          <span>มีความประสงค์ขอลา</span><Fill value={leaveWord(r.lt_name)} />
          <span>เนื่องจาก</span><Fill value={r.lr_reason} grow={1.6} />
        </div>
        <div className="lf-line">
          <span>ตั้งแต่วันที่</span><Fill value={fmtDateTH(r.lr_start_date)} grow={1.3} />
          <span>ถึงวันที่</span><Fill value={fmtDateTH(r.lr_end_date)} grow={1.3} />
          <span>มีกำหนด</span><Fill value={fmtDays(r.lr_days)} grow={0.5} />
          <span>วัน</span>
        </div>
        <div className="lf-line">
          <span>ข้าพเจ้าได้ลา</span><Fill value={data.lastLeave && leaveWord(data.lastLeave.lt_name)} />
          <span>ครั้งสุดท้ายเมื่อวันที่</span><Fill value={data.lastLeave && fmtDateTH(data.lastLeave.lr_start_date)} grow={1.2} />
          <span>ถึงวันที่</span><Fill value={data.lastLeave && fmtDateTH(data.lastLeave.lr_end_date)} grow={1.2} />
        </div>
        <div className="lf-line">
          <span>ในระหว่างลาติดต่อข้าพเจ้าได้ที่</span><Fill value={contact} grow={3} />
        </div>

        <div className="lf-sign">
          <div>ขอแสดงความนับถือ</div>
          <div className="lf-sign-space" />
          <div className="lf-sign-dots">...............................................</div>
          <div>( {r.t_name} )</div>
        </div>

        <div className="lf-bottom">
          <div className="lf-bottom-left">
            <div className="lf-line lf-plain lf-stat-head">
              <span>สถิติการลาในปีการศึกษา</span><Fill value={data.academicYear + 543} grow={0.6} />
            </div>
            <table className="lf-table">
              <thead>
                <tr>
                  <th>ประเภทลา</th>
                  <th>ลามาแล้ว<br />(วันทำการ)</th>
                  <th>ลาครั้งนี้<br />(วันทำการ)</th>
                  <th>รวมเป็น<br />(วันทำการ)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(s => {
                  const before = parseFloat(s.used_days) || 0
                  const now = s.lt_id === r.tb_hr_leave_type_lt_id ? thisDays : 0
                  return (
                    <tr key={s.lt_id}>
                      <td>{s.lt_name}</td>
                      <td className="lf-num">{before ? fmtDays(before) : ''}</td>
                      <td className="lf-num">{now ? fmtDays(now) : ''}</td>
                      <td className="lf-num">{before + now ? fmtDays(before + now) : ''}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <div className="lf-checker">
              <div>(ลงชื่อ) .................................................... ผู้ตรวจสอบ</div>
              <div className="lf-checker-name">({data.hrHeadName || '................................'})</div>
              <div className="lf-checker-name">หัวหน้าฝ่ายบุคลากร</div>
            </div>

            <div className="lf-note">
              <span className="lf-note-label">หมายเหตุ</span>
              <span>1. ลาป่วยเกิน 3 วัน ต้องมีใบรับรองแพทย์</span>
              <div className="lf-note-2">2. ลากิจ ให้ลาก่อนล่วงหน้า 1 วัน</div>
            </div>
          </div>

          <div className="lf-bottom-right">
            <div className="lf-order-title">คำสั่ง</div>
            <div className="lf-check">
              <span className={`lf-box${r.lr_status === 'approved' ? ' lf-box-on' : ''}`} />
              <span>อนุญาต</span>
            </div>
            <div className="lf-check">
              <span className={`lf-box${r.lr_status === 'rejected' ? ' lf-box-on' : ''}`} />
              <span>ไม่อนุญาต เพราะ</span>
              <span className="lf-fill lf-reject-note">
                {r.lr_status === 'rejected' ? (r.approve_note || '') : ''}
              </span>
            </div>
            <div className="lf-order-sign">
              <div>(ลงชื่อ) ....................................................</div>
              <div className="lf-checker-name">( {school.principal_name} )</div>
              <div className="lf-checker-name">ผู้อำนวยการ{school.school_name}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
